import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import { groupByMonth } from '@/utils/groupByMonth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stages = ['Cutting', 'Forging', 'Machining', 'HT', 'FG'];

export default function StageIssue() {
  const [issues, setIssues] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    part_id: '',
    stage_from: '',
    stage_to: '',
    vendor_id: '',
    qty: ''
  });

  const fetchIssues = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions/stage-issue`, {
        
      });
      setIssues(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load stage issues');
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/items`, {
        
      });
      setItems(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/vendors`, {
        
      });
      setVendors(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchIssues();
    fetchItems();
    fetchVendors();
  }, [fetchIssues, fetchItems, fetchVendors]);

  const resetForm = () => {
    setFormData({ date: new Date().toISOString().split('T')[0], part_id: '', stage_from: '', stage_to: '', vendor_id: '', qty: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (entry) => {
    setFormData({
      date: new Date(entry.date).toISOString().split('T')[0],
      part_id: entry.part_id,
      stage_from: entry.stage_from,
      stage_to: entry.stage_to,
      vendor_id: entry.vendor_id || '',
      qty: entry.qty.toString()
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const data = {
        date: new Date(formData.date).toISOString(),
        part_id: formData.part_id,
        stage_from: formData.stage_from,
        stage_to: formData.stage_to,
        vendor_id: formData.vendor_id || null,
        qty: parseInt(formData.qty),
        created_by: user.username
      };

      if (editingId) {
        await axios.put(`${API}/transactions/stage-issue/${editingId}`, data, {
          
        });
        toast.success('Stage issue updated successfully');
      } else {
        await axios.post(`${API}/transactions/stage-issue`, data, {
          
        });
        toast.success('Stage issue created successfully');
      }
      resetForm();
      fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stage issue');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this stage issue?')) return;
    try {
      await axios.delete(`${API}/transactions/stage-issue/${id}`, {
        
      });
      toast.success('Stage issue deleted successfully');
      fetchIssues();
    } catch (error) {
      toast.error('Failed to delete stage issue');
    }
  };

  const toggleMonth = (key) => setCollapsedMonths(prev => ({...prev, [key]: !prev[key]}));
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const monthGroups = groupByMonth(issues);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="stage-issue-title">STAGE ISSUE</h1>
          <p className="text-sm text-gray-700 mt-2">Issue parts to next stage or vendor</p>
        </div>
        <Button
          data-testid="toggle-issue-form-button"
          onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
          className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
        >
          {showForm ? <><X className="w-4 h-4 mr-2" />CANCEL</> : <><Plus className="w-4 h-4 mr-2" />NEW ISSUE</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>{editingId ? 'EDIT STAGE ISSUE' : 'NEW STAGE ISSUE'}</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input
                data-testid="issue-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part ID</Label>
              <SearchableSelect
                testId="issue-part-id-select"
                className="mt-1.5"
                options={items.map(item => ({ value: item.part_id, label: `${item.part_id} - ${item.part_name}`, searchStr: `${item.part_id} ${item.part_name}` }))}
                value={formData.part_id}
                onValueChange={(value) => setFormData({...formData, part_id: value})}
                placeholder="Search part..."
                searchPlaceholder="Type to search parts..."
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
              <Input
                data-testid="issue-qty-input"
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData({...formData, qty: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Stage From</Label>
              <Select
                value={formData.stage_from}
                onValueChange={(value) => setFormData({...formData, stage_from: value})}
                required
              >
                <SelectTrigger data-testid="issue-stage-from-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map(stage => (
                    <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Stage To</Label>
              <Select
                value={formData.stage_to}
                onValueChange={(value) => setFormData({...formData, stage_to: value})}
                required
              >
                <SelectTrigger data-testid="issue-stage-to-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map(stage => (
                    <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Vendor (Optional)</Label>
              <Select
                value={formData.vendor_id || undefined}
                onValueChange={(value) => setFormData({...formData, vendor_id: value})}
              >
                <SelectTrigger data-testid="issue-vendor-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder="None (Internal)" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>{vendor.vendor_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3 flex justify-end pt-4">
              <Button
                data-testid="submit-issue-button"
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                {editingId ? 'UPDATE ISSUE' : 'CREATE ISSUE'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECENT ISSUES</h2>
        </div>
        {issues.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No stage issues found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage From</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage To</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              {monthGroups.map(group => (
                <tbody key={`group-${group.key}`}>
                  <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleMonth(group.key)}>
                    <td colSpan={7} className="p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                        {collapsedMonths[group.key] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {group.label} ({group.entries.length} entries)
                      </div>
                    </td>
                  </tr>
                  {!collapsedMonths[group.key] && group.entries.map((issue, idx) => (
                    <tr key={issue.id || idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`issue-row-${idx}`}>
                      <td className="p-4 align-middle font-mono text-xs">{new Date(issue.date).toLocaleDateString()}</td>
                      <td className="p-4 align-middle font-mono text-xs">{issue.part_id}</td>
                      <td className="p-4 align-middle">
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-slate-100 text-slate-700">
                          {issue.stage_from}
                        </span>
                      </td>
                      <td className="p-4 align-middle">
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-emerald-50 text-emerald-700">
                          {issue.stage_to}
                        </span>
                      </td>
                      <td className="p-4 align-middle font-mono text-xs">{issue.vendor_id || 'Internal'}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs">{issue.qty}</td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center justify-center gap-1">
                          {canModify(issue) && (<>
                            <button
                              data-testid={`edit-issue-${idx}`}
                              onClick={() => handleEdit(issue)}
                              className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`delete-issue-${idx}`}
                              onClick={() => handleDelete(issue.id)}
                              className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}